import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Center,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Spinner,
  Spacer,
} from "@chakra-ui/react";
import { KeyIcon, MailIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { getHighestRoleInString, useSessionState } from "./SessionService";
import { toast } from "react-toastify";

const delay = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

function isValidEmailAddress(emailAddress: string) {
  return emailAddress.match(/.+@.+\..+/) != null;
}

const hasCorrectLength = (str: string) => str.length > 7;
const containsNumber = (str: string) => str.match(/.*[0-9].*/) != null;
const containsCCL = (str: string) => str.match(/.*[A-ZÅÄÖ].*/) != null;
const containsLCL = (str: string) => str.match(/.*[a-zåäö].*/) != null;

function verifyPassword(password: string) {
  return (
    hasCorrectLength(password) &&
    containsNumber(password) &&
    containsCCL(password) &&
    containsLCL(password)
  );
}

export function LoginModal(props: {
  onClose: () => void;
  fillScreen: boolean;
}) {
  const session = useSessionState();
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  return (
    <Modal
      closeOnOverlayClick={false}
      scrollBehavior={"inside"}
      isOpen={true}
      size={props.fillScreen ? "full" : "5xl"}
      onClose={() => {
        props.onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent background={"gray.800"} textColor={"white"}>
        <ModalHeader>
          <Center>
            <Heading>
              {showCreateUserModal
                ? "Skapa användare"
                : showChangePasswordModal
                ? "Uppdatera lösenord"
                : session.role
                ? "Logga ut"
                : "Logga in"}
            </Heading>
          </Center>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody scrollBehavior={"auto"} margin={"20px"}>
          {showCreateUserModal ? (
            <SignupModalBody
              onAbort={() => {
                setShowCreateUserModal(false);
                setShowChangePasswordModal(false);
              }}
              onClose={props.onClose}
            />
          ) : showChangePasswordModal ? (
            <ChangePasswordModalBody
              onAbort={() => {
                setShowCreateUserModal(false);
                setShowChangePasswordModal(false);
              }}
              onClose={props.onClose}
            />
          ) : session.role ? (
            <LogoutModalBody onClose={props.onClose} />
          ) : (
            <VStack>
              <LoginModalBody onClose={props.onClose} />
              <VStack
                width={"100%"}
                marginTop={"100px"}
                alignItems="flex-start"
                spacing={"40px"}
              >
                <Button
                  onClick={() => setShowCreateUserModal(true)}
                  textColor={"red.600"}
                  variant={"link"}
                >
                  Skapa användare
                </Button>
                <Button
                  onClick={() => setShowChangePasswordModal(true)}
                  textColor={"red.600"}
                  variant={"link"}
                >
                  Byt lösenord
                </Button>
              </VStack>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter alignContent={"left"}></ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function LoginModalBody(props: { onClose: () => void }) {
  const session = useSessionState();
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const submit = useCallback(() => {
    const pwdCopy = password;
    setPassword("");
    session.login(username.trim(), pwdCopy).then(async (r) => {
      const role = getHighestRoleInString(r.roles);
      if (role) {
        props.onClose();
      } else {
        await delay(1000);
        toast.info(`Inloggning misslyckades`);
      }
    });
  }, [username, password, setPassword, session.login]);

  return (
    <VStack>
      <VStack spacing={"30px"}>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <MailIcon color="orange" opacity={"80%"} />
          </InputLeftElement>
          <Input
            placeholder="E-mail adress"
            type="email"
            value={username}
          autoComplete="email webauth"
            onChange={(e) => {
              setUsername(e.currentTarget.value);
            }}
          />
        </InputGroup>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <KeyIcon color="yellow" opacity={"80%"} />
          </InputLeftElement>
          <Input
            placeholder="Lösenord"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </InputGroup>
        <HStack>
          <Button
            borderRadius={"20px"}
            variant={"outline"}
            aria-label={"login-button"}
            colorScheme={"messenger"}
            disabled={
              !isValidEmailAddress(username.trim()) || password.length == 0
            }
            onClick={submit}
          >
            Logga in
          </Button>
        </HStack>
      </VStack>
    </VStack>
  );
}

function LogoutModalBody(props: { onClose: () => void }) {
  const session = useSessionState();
  return (
    <Center>
      <HStack spacing={3}>
        <Button
          borderRadius={"20px"}
          variant={"outline"}
          aria-label={"logout-button"}
          colorScheme={"messenger"}
          onClick={() => {
            session.logout().then(() => {
              props.onClose();
            });
          }}
        >
          {"Logga ut " + session.user}
        </Button>
      </HStack>
    </Center>
  );
}

function ChangePasswordModalBody(props: {
  onAbort: () => void;
  onClose: () => void;
}) {
  const session = useSessionState();
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [responseStatus, setResponseStatus] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);

  const [usernameErrorMessage, setUsernameErrorMessage] = useState<
    string | null
  >(null);

  const [loading, setLoading] = useState(false);

  return (
    <Center>
      <HStack>
        <VStack spacing={3} hidden={responseMessage != null}>
          <VStack>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <MailIcon
                  color={isValidEmailAddress(username) ? "green" : "gray"}
                />
              </InputLeftElement>
              <Input
                placeholder="e-postadress"
                type="email"
                value={username}
                disabled={loading}
                onChange={(e) => {
                  const emailAddress = e.currentTarget.value;
                  setUsername(emailAddress);
                  if (isValidEmailAddress(emailAddress)) {
                    setUsernameErrorMessage(null);
                  } else {
                    setUsernameErrorMessage(
                      "Ange din e-postadress! Den angivna adressen har fel format."
                    );
                  }
                }}
              />
            </InputGroup>
            <Text fontSize={"10px"} textColor={"red"}>
              {usernameErrorMessage || ""}
            </Text>
          </VStack>
          <VStack>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <KeyIcon color={verifyPassword(password) ? "green" : "gray"} />
              </InputLeftElement>
              <Input
                placeholder="Nytt lösenord"
                type="password"
                value={password}
                disabled={loading}
                onChange={(e) => {
                  setPassword(e.currentTarget.value);
                }}
              />
            </InputGroup>
            <HStack>
              <Text
                fontSize={"10px"}
                fontWeight={"bold"}
                decoration={containsLCL(password) ? "line-through" : undefined}
                textColor={containsLCL(password) ? "gray.500" : "white"}
              >
                a-ö,{" "}
              </Text>
              <Text
                fontSize={"10px"}
                fontWeight={"bold"}
                decoration={containsCCL(password) ? "line-through" : undefined}
                textColor={containsCCL(password) ? "gray.500" : "white"}
              >
                A-Ö,{" "}
              </Text>
              <Text
                fontSize={"10px"}
                fontWeight={"bold"}
                decoration={
                  containsNumber(password) ? "line-through" : undefined
                }
                textColor={containsNumber(password) ? "gray.500" : "white"}
              >
                0-9,{" "}
              </Text>
              <Text
                fontSize={"10px"}
                fontWeight={"bold"}
                decoration={
                  hasCorrectLength(password) ? "line-through" : undefined
                }
                textColor={hasCorrectLength(password) ? "gray.500" : "white"}
              >
                längd (minst 8)
              </Text>
            </HStack>
          </VStack>
          <HStack>
            <Button
              marginTop={"20px"}
              borderRadius={"20px"}
              variant={"outline"}
              aria-label={"logout-button"}
              colorScheme={"messenger"}
              disabled={
                !isValidEmailAddress(username) || !verifyPassword(password)
              }
              onClick={() => {
                setLoading(true);

                session
                  .changePassword(username, password)
                  .then(async (response) => {
                    await delay(500);
                    setResponseMessage(response[0] as string);
                    setResponseStatus(response[1] as boolean);
                    setLoading(false);
                    setPassword("");
                  });
              }}
            >
              <>
                {"Skicka"}
                {loading && <Spinner marginLeft={"20px"} color="white" />}
              </>
            </Button>
          </HStack>
        </VStack>
        <VStack hidden={responseMessage == null}>
          <Text whiteSpace="pre-line">{responseMessage}</Text>
          <HStack marginTop={"40px"}>
            <Button
              hidden={responseStatus}
              colorScheme="red"
              variant={"ghost"}
              onClick={() => setResponseMessage(null)}
            >
              Tillbaka
            </Button>
          </HStack>
        </VStack>
      </HStack>
    </Center>
  );
}

function SignupModalBody(props: { onAbort: () => void; onClose: () => void }) {
  const session = useSessionState();
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [responseStatus, setResponseStatus] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);

  const [usernameErrorMessage, setUsernameErrorMessage] = useState<
    string | null
  >(null);

  const [loading, setLoading] = useState(false);

  return (
    <Center>
      <HStack>
        <VStack spacing={3} hidden={responseMessage != null}>
          <VStack>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <MailIcon
                  color={isValidEmailAddress(username) ? "green" : "gray"}
                />
              </InputLeftElement>
              <Input
                placeholder="e-postadress"
                type="email"
                value={username}
                disabled={loading}
                onChange={(e) => {
                  const emailAddress = e.currentTarget.value;
                  setUsername(emailAddress);
                  if (isValidEmailAddress(emailAddress)) {
                    setUsernameErrorMessage(null);
                  } else {
                    setUsernameErrorMessage(
                      "Ange din e-postadress! Den angivna adressen har fel format."
                    );
                  }
                }}
              />
            </InputGroup>
            <Text fontSize={"10px"} textColor={"red"}>
              {usernameErrorMessage || ""}
            </Text>
          </VStack>
          <VStack>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <KeyIcon color={verifyPassword(password) ? "green" : "gray"} />
              </InputLeftElement>
              <Input
                placeholder="Lösenord"
                type="password"
                value={password}
                disabled={loading}
                onChange={(e) => {
                  setPassword(e.currentTarget.value);
                }}
              />
            </InputGroup>
            <HStack>
              <Text
                fontSize={"10px"}
                fontWeight={"bold"}
                decoration={containsLCL(password) ? "line-through" : undefined}
                textColor={containsLCL(password) ? "gray.500" : "white"}
              >
                a-ö,{" "}
              </Text>
              <Text
                fontSize={"10px"}
                fontWeight={"bold"}
                decoration={containsCCL(password) ? "line-through" : undefined}
                textColor={containsCCL(password) ? "gray.500" : "white"}
              >
                A-Ö,{" "}
              </Text>
              <Text
                fontSize={"10px"}
                fontWeight={"bold"}
                decoration={
                  containsNumber(password) ? "line-through" : undefined
                }
                textColor={containsNumber(password) ? "gray.500" : "white"}
              >
                0-9,{" "}
              </Text>
              <Text
                fontSize={"10px"}
                fontWeight={"bold"}
                decoration={
                  hasCorrectLength(password) ? "line-through" : undefined
                }
                textColor={hasCorrectLength(password) ? "gray.500" : "white"}
              >
                längd (minst 8)
              </Text>
            </HStack>
          </VStack>
          <HStack>
            <Button
              marginTop={"20px"}
              borderRadius={"20px"}
              variant={"outline"}
              aria-label={"logout-button"}
              colorScheme={"messenger"}
              disabled={
                !isValidEmailAddress(username) || !verifyPassword(password)
              }
              onClick={() => {
                setLoading(true);

                session.signup(username, password).then(async (response) => {
                  await delay(500);
                  setResponseMessage(response[0] as string);
                  setResponseStatus(response[1] as boolean);
                  setLoading(false);
                  setPassword("");
                });
              }}
            >
              <>
                {"Skapa användare"}
                {loading && <Spinner marginLeft={"20px"} color="white" />}
              </>
            </Button>
          </HStack>
        </VStack>
        <VStack hidden={responseMessage == null}>
          <Text whiteSpace="pre-line">{responseMessage}</Text>
          <HStack marginTop={"40px"}>
            <Button
              hidden={responseStatus}
              colorScheme="red"
              variant={"ghost"}
              onClick={() => setResponseMessage(null)}
            >
              Tillbaka
            </Button>
          </HStack>
        </VStack>
      </HStack>
    </Center>
  );
}
